/**
 * The timeline's selection (1.7.3): several items at once, plus the one the Inspector shows (`primary`). Pure helpers
 * only; the timeline and the editor hold the state. Items are the contracts' ItemRef (a gap is named by the clip after
 * it, so compare with itemKey). Clicking a linked item selects its whole link group (linkedItems); Option-click picks
 * the one item. Rules: docs/dev/editor-1.7.3-build.md.
 */
import { allItemRefs, itemKey, itemSpan, linkedItems, type ItemRef, type SequenceDocument, type Span } from '@prism/contracts';

export interface TimelineSelection {
  items: ItemRef[];
  /** The item last clicked (the Inspector shows it when it is the only one); null when nothing is selected. */
  primary: ItemRef | null;
}

export const NO_SELECTION: TimelineSelection = { items: [], primary: null };

/** Modifier keys of a click on an item. `toggle` is Shift; `add` is ⌘ (Ctrl elsewhere); `solo` is Option (Alt). */
export interface PickMods { shift?: boolean; meta?: boolean; alt?: boolean }

const same = (a: ItemRef, b: ItemRef) => a.kind === b.kind && a.id === b.id;
export const isSelected = (sel: TimelineSelection, ref: ItemRef): boolean => sel.items.some((r) => same(r, ref));
/** Whether any item with this id is selected (a clip's lanes: V1 and its A1 share the clip's id). */
export const isSelectedId = (sel: TimelineSelection, kind: ItemRef['kind'], id: string): boolean => sel.items.some((r) => r.kind === kind && r.id === id);

/** Items without repeats, in their first order. */
export function uniqueItems(items: readonly ItemRef[]): ItemRef[] {
  const seen = new Set<string>();
  return items.filter((r) => { const k = itemKey(r); if (seen.has(k)) return false; seen.add(k); return true; });
}

/** An item and its link group (gaps, cues and markers have none), or the item alone with `solo`. */
export function groupOf(doc: SequenceDocument, ref: ItemRef, solo = false): ItemRef[] {
  if (solo || ref.kind === 'gap') return [ref];
  return linkedItems(doc, [ref]);
}

/** Only this item (and its link group unless `solo`). */
export function selectOnly(doc: SequenceDocument, ref: ItemRef, solo = false): TimelineSelection {
  return { items: groupOf(doc, ref, solo), primary: ref };
}

/** The selection with these items added; `primary` becomes `ref`. */
export function addItems(sel: TimelineSelection, items: readonly ItemRef[], primary: ItemRef | null = items[0] ?? null): TimelineSelection {
  return { items: uniqueItems([...sel.items, ...items]), primary: primary ?? sel.primary };
}

/** The selection without these items; `primary` falls back to the last item left. */
export function removeItems(sel: TimelineSelection, items: readonly ItemRef[]): TimelineSelection {
  const keys = new Set(items.map(itemKey));
  const left = sel.items.filter((r) => !keys.has(itemKey(r)));
  const primary = sel.primary && !keys.has(itemKey(sel.primary)) ? sel.primary : left.at(-1) ?? null;
  return { items: left, primary };
}

/** Shift-click or ⌘-click (1.7.3): an unselected item (and its group) is added; a selected one (and its group) leaves. */
export function toggleItem(doc: SequenceDocument, sel: TimelineSelection, ref: ItemRef, solo = false): TimelineSelection {
  const group = groupOf(doc, ref, solo);
  return isSelected(sel, ref) ? removeItems(sel, group) : addItems(sel, group, ref);
}

/** A click (no drag) on an item with these modifiers. */
export function clickItem(doc: SequenceDocument, sel: TimelineSelection, ref: ItemRef, mods: PickMods = {}): TimelineSelection {
  if (mods.shift || mods.meta) return toggleItem(doc, sel, ref, mods.alt);
  return selectOnly(doc, ref, mods.alt);
}

/**
 * Pressing the pointer on an item (1.7.3). A press may become a click or a drag, so it gives three selections:
 *  - `now`: at once (the item is shown selected while the pointer is down);
 *  - `click`: when the pointer comes up without a drag (null: stays `now`);
 *  - `drag`: what moves when it becomes a drag (`drag.items` is the moving set).
 * Rules: an unselected item is selected at once (Shift adds it to the others); a selected item keeps the selection,
 * so dragging it moves them all, and a click then selects it alone (Shift or ⌘: removes it). ⌘ on an unselected item
 * adds it on a click, but a ⌘-drag (insert) moves that item alone. Option (Alt) takes the one item without its link
 * group, always alone (an Option-drag slips one clip).
 */
export function pressItem(doc: SequenceDocument, sel: TimelineSelection, ref: ItemRef, mods: PickMods = {}): { now: TimelineSelection; click: TimelineSelection | null; drag: TimelineSelection } {
  if (mods.alt && !mods.shift && !mods.meta) {
    const only = selectOnly(doc, ref, true);
    return { now: only, click: null, drag: only };
  }
  const selected = isSelected(sel, ref);
  const group = groupOf(doc, ref, mods.alt);
  if (mods.shift) {
    if (selected) return { now: { ...sel, primary: ref }, click: removeItems(sel, group), drag: { ...sel, primary: ref } };
    const now = addItems(sel, group, ref);
    return { now, click: null, drag: now };
  }
  if (mods.meta) {
    if (selected) return { now: { ...sel, primary: ref }, click: removeItems(sel, group), drag: { ...sel, primary: ref } };
    return { now: sel, click: addItems(sel, group, ref), drag: { items: group, primary: ref } };
  }
  if (selected) {
    const keep = { ...sel, primary: ref };
    const only = { items: group, primary: ref };
    return { now: keep, click: sel.items.length === group.length ? null : only, drag: keep };
  }
  const only = { items: group, primary: ref };
  return { now: only, click: null, drag: only };
}

/** ⌘A: every item but gaps. */
export function selectAll(doc: SequenceDocument): TimelineSelection {
  const items = allItemRefs(doc);
  return { items, primary: items[0] ?? null };
}

/** The selection with items no longer in the document left out (after an edit or an undo); the same object when all are there. */
export function pruneSelection(doc: SequenceDocument, sel: TimelineSelection): TimelineSelection {
  if (!sel.items.length) return sel;
  const items = sel.items.filter((r) => itemSpan(doc, r) !== null);
  if (items.length === sel.items.length && (!sel.primary || itemSpan(doc, sel.primary) !== null)) return sel;
  const primary = sel.primary && items.some((r) => same(r, sel.primary!)) ? sel.primary : items.at(-1) ?? null;
  return { items, primary };
}

/** The single item the Inspector shows: the only one selected, or null with none or several. */
export function singleItem(sel: TimelineSelection): ItemRef | null {
  return sel.items.length === 1 ? sel.items[0]! : null;
}

/** From the earliest start to the latest end of the items, or null when none is in the document. */
export function selectionSpan(doc: SequenceDocument, items: readonly ItemRef[]): Span | null {
  let out: Span | null = null;
  for (const r of items) {
    const s = itemSpan(doc, r);
    if (!s) continue;
    out = out ? { start: Math.min(out.start, s.start), end: Math.max(out.end, s.end) } : { ...s };
  }
  return out;
}

/* ---------------------------------------------------------------- the rubber band */

export interface Box { left: number; top: number; right: number; bottom: number }

/** The box between two pointer positions (any direction). */
export function boxOf(a: { x: number; y: number }, b: { x: number; y: number }): Box {
  return { left: Math.min(a.x, b.x), top: Math.min(a.y, b.y), right: Math.max(a.x, b.x), bottom: Math.max(a.y, b.y) };
}

/** Whether a rubber band this small is a click on empty space instead (pixels). */
export const isTinyBox = (b: Box, px = 3): boolean => b.right - b.left < px && b.bottom - b.top < px;

/**
 * The items a rubber band touches (1.7.3): every drawn item whose box overlaps it (touching an edge counts; a marker
 * is a line, so its box may have no width). `rects` are the items' drawn boxes, in the band's coordinates; an item
 * drawn twice (a V1 clip on V1 and A1) is listed once.
 */
export function itemsInBox(rects: ReadonlyArray<{ ref: ItemRef } & Box>, box: Box): ItemRef[] {
  return uniqueItems(rects.filter((r) => r.left <= box.right && r.right >= box.left && r.top <= box.bottom && r.bottom >= box.top).map((r) => r.ref));
}

/** "video:c1" (an element's data-item) back to an item, or null. */
export function parseItemKey(key: string | null | undefined): ItemRef | null {
  if (!key) return null;
  const i = key.indexOf(':');
  if (i <= 0) return null;
  const kind = key.slice(0, i);
  if (!['video', 'audio', 'layer', 'graphic', 'cue', 'marker', 'gap'].includes(kind)) return null;
  return { kind: kind as ItemRef['kind'], id: key.slice(i + 1) };
}

/** A short count of the selected items for the Inspector ("3 clips, 1 graphic"). */
export function describeItems(items: readonly ItemRef[]): string {
  const n = { clip: 0, graphic: 0, cue: 0, marker: 0, gap: 0 };
  for (const r of items) {
    if (r.kind === 'video' || r.kind === 'audio' || r.kind === 'layer') n.clip++;
    else n[r.kind]++;
  }
  const word = (k: number, one: string) => `${k} ${one}${k === 1 ? '' : 's'}`;
  return [n.clip && word(n.clip, 'clip'), n.graphic && word(n.graphic, 'graphic'), n.cue && word(n.cue, 'subtitle'), n.marker && word(n.marker, 'marker'), n.gap && word(n.gap, 'gap')]
    .filter(Boolean).join(', ');
}
